import { useCallback, useContext, useRef, useState } from 'react'
import Button from '../../../components/Button'
import ButtonsOnBottom from '../../../components/ButtonsOnBottom'
import Content from '../../../components/Content'
import ErrorMessage from '../../../components/Error'
import FlexCol from '../../../components/FlexCol'
import FlexRow from '../../../components/FlexRow'
import Header from '../../../components/Header'
import LoadingLogo from '../../../components/LoadingLogo'
import Modal from '../../../components/Modal'
import Padded from '../../../components/Padded'
import Text from '../../../components/Text'
import { NavigationContext, Pages } from '../../../providers/navigation'
import { FlowContext } from '../../../providers/flow'
import { WalletContext } from '../../../providers/wallet'
import { consoleError } from '../../../lib/logs'
import { extractError } from '../../../lib/error'
import Input from '../../../components/Input'
import AssetCard from '../../../components/AssetCard'
import { atomicToUnits, prettyAssetAmount, unitsToAtomic } from '../../../lib/assets'
import { saveTransactionActivityMetadata } from '../../../lib/storage'
import { useTranslation } from '../../../providers/language'

export default function AppAssetBurn() {
  const { replace } = useContext(NavigationContext)
  const { assetInfo } = useContext(FlowContext)
  const { assetBalances, svcWallet, reloadWallet } = useContext(WalletContext)
  const { t } = useTranslation()

  const [amount, setAmount] = useState(BigInt(0))
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState(false)
  const [opDone, setOpDone] = useState(false)
  const pendingNav = useRef<() => void>()
  const pendingConfirm = useRef(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const name = assetInfo.metadata?.name ?? t('mint.unknown')
  const ticker = assetInfo.metadata?.ticker ?? assetInfo.assetId.slice(0, 8)
  const icon = assetInfo.metadata?.icon
  const decimals = assetInfo.metadata?.decimals ?? 0
  const balance = assetBalances.find((a) => a.assetId === assetInfo.assetId)?.amount ?? BigInt(0)

  const handleAmountChange = (value: string) => {
    const cents = unitsToAtomic(value, decimals)
    setAmount(cents)
  }

  const handleBurnRequest = () => {
    if (!amount || amount <= 0) {
      setError(t('mint.amountMustBePositive'))
      return
    }
    if (amount > balance) {
      setError(t('mint.exceedsBalance', { balance: prettyAssetAmount(balance, decimals), ticker }))
      return
    }
    setError('')
    setShowConfirm(true)
  }

  const processBurn = async () => {
    if (!svcWallet) return

    setProcessing(true)
    setError('')

    try {
      const txid = await svcWallet.assetManager.burn({ assetId: assetInfo.assetId, amount })
      saveTransactionActivityMetadata(txid, { assetAction: 'burned' })
      await reloadWallet()
      pendingNav.current = () => replace(Pages.AppAssetDetail, Pages.AppAssets)
      setOpDone(true)
    } catch (err) {
      consoleError(err, 'error burning asset')
      setError(extractError(err))
      setProcessing(false)
    }
  }

  const handleBurnConfirm = () => {
    pendingConfirm.current = true
    setShowConfirm(false)
  }

  const handleConfirmExitComplete = () => {
    if (!pendingConfirm.current) return
    pendingConfirm.current = false
    void processBurn()
  }

  const handleExitComplete = useCallback(() => {
    pendingNav.current?.()
  }, [])

  const handleMax = () => setAmount(balance)

  if (processing || opDone)
    return (
      <LoadingLogo text={t('loading.burning')} done={opDone} exitMode='fly-up' onExitComplete={handleExitComplete} />
    )

  return (
    <>
      <Header text={t('mint.burnTitle', { ticker: ticker || name })} back />
      <Modal open={showConfirm} onOpenChange={setShowConfirm} onExitComplete={handleConfirmExitComplete}>
        <FlexCol gap='1.5rem'>
          <FlexCol centered gap='0.5rem'>
            <Text big bold>
              {t('mint.confirmBurn')}
            </Text>
            <Text centered wrap color='neutral-500'>
              {t('mint.burnWarning', { amount: prettyAssetAmount(amount, decimals), ticker: ticker || name })}
            </Text>
          </FlexCol>
          <FlexRow>
            <Button onClick={() => setShowConfirm(false)} label={t('common.cancel')} secondary />
            <Button onClick={handleBurnConfirm} label={t('mint.burn')} />
          </FlexRow>
        </FlexCol>
      </Modal>
      <Content>
        <Padded>
          <FlexCol gap='1rem'>
            <ErrorMessage error={Boolean(error)} text={error} />
            <AssetCard
              assetId={assetInfo.assetId}
              balance={balance}
              decimals={decimals}
              icon={icon}
              name={name}
              ticker={ticker}
            />
            <Input
              right={
                <span
                  onClick={handleMax}
                  data-testid='burn-max-button'
                  style={{ color: 'var(--purpletext)', fontSize: 13, cursor: 'pointer' }}
                >
                  {t('mint.max')}
                </span>
              }
              min='0'
              step='1'
              type='number'
              placeholder='0'
              label={t('mint.amountToBurn')}
              onChange={handleAmountChange}
              value={amount ? atomicToUnits(amount, decimals) : ''}
            />
          </FlexCol>
        </Padded>
      </Content>
      <ButtonsOnBottom>
        <Button label={t('mint.burn')} onClick={handleBurnRequest} disabled={amount <= 0} />
      </ButtonsOnBottom>
    </>
  )
}
